/**
 * Single source of truth for operator text mapping and tag variant mapping
 * Used by both ConditionsRenderer and RuleCondition components
 */

/**
 * Map condition type to tag variant for visual display
 *
 * @param type - The condition type from JSON Logic ('cluster_name', 'node_group_name', 'instance_id', 'tags')
 * @returns Tag variant for styling
 */
export function getTagVariant(
  type: string
): 'cluster' | 'node' | 'instance' | 'instance_tag' | 'volume' {
  switch (type) {
    case 'cluster_name':
      return 'cluster';
    case 'node_group_name':
      return 'node';
    case 'instance_id':
      return 'instance';
    case 'instance_tags':
      return 'instance';
      return 'instance_tag';
    case 'tags':
      return 'volume';
    default:
      return 'volume';
  }
}

/**
 * Map operator codes to human-readable text based on condition type
 *
 * @param operator - The operator code from parsed JSON Logic ('in', '!', 'some', 'none', 'some-contains', 'none-contains')
 * @param conditionType - The condition type ('tags', 'instance_id', 'cluster_name', 'node_group_name')
 * @returns Human-readable operator text
 */
export function getOperatorText(operator: string, conditionType: string): string {
  // For tags, differentiate between exact match (in) and contains (or)
  // Tags use 'some'/'none' because they iterate over an array
  if (conditionType === 'tags') {
    switch (operator) {
      case 'some':
        return 'is'; // Tags exact match: { some: [{ var: "tags" }, { in: [...] }] }
      case 'some-contains':
        return 'contains'; // Tags contains: { some: [{ var: "tags" }, { or: [...] }] }
      case 'none':
        return 'is not'; // Tags exact exclude: { none: [{ var: "tags" }, { in: [...] }] }
      case 'none-contains':
        return "doesn't contain"; // Tags doesn't contain: { none: [{ var: "tags" }, { or: [...] }] }
      default:
        return 'is';
    }
  }

  // For non-tag fields (instance_id, cluster_name, node_group_name)
  // These are single-value fields
  switch (operator) {
    case 'in':
      return 'is'; // Exact match: { in: [{ var: "instance_id" }, ["i-123"]] }
    case '!':
      return 'is not'; // Exact exclusion: { "!": { in: [...] } }
    case 'some-contains':
      return 'contains'; // Contains: { some: [["pattern"], { in: [...] }] }
    case 'none-contains':
      return "doesn't contain"; // Doesn't contain: { none: [["pattern"], { in: [...] }] }
    default:
      return 'is';
  }
}
